/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react';
import { getStaticPaths as getProductStaticPaths } from './products/[index].static';
import _uniqWith from 'lodash/uniqWith';
import { TemplateSwitch } from 'src/templates';
import { CmsComponents, ContentfulTemplates } from 'src/lib/constants';
import { generateBlogExcerptProps } from 'src/lib/utils';
import {
  findMatchingHardcodedPage,
  getLegacyLocaleFromPath,
  getLegacyPageSlugFromPath,
  getLocaleFromPath,
  makeLegacyLocalizedRequests,
  makeNewTemplateRequestsForAllLocales,
  makeNewTemplateRequestsForSingleLocale,
  makeSingleLegacyLocalizedRequest,
  mapEntryToProps,
  mapResponseToPaths,
  getPathWithoutLocale,
} from 'src/lib/router-utils';
import { enEuRootPages } from 'src/store/en-eu/localized-pages';
import { enGbRootPages } from 'src/store/en-gb/localized-pages';
import { augmentPlaceholderData } from 'src/components-v3/Placeholder/placeholderHelpers';

// hopefully one day we can remove these too!
const hardcodedRootPages = [...enEuRootPages, ...enGbRootPages];

const StaticCmsPage = (props) => {
  return <TemplateSwitch {...props} />;
};

// Static build: uses getStaticPaths to pre-generate all CMS pages at build time
export async function getStaticPaths() {
  // sadly, we still need to support legacy templates
  // these should all be considered deprecated and removed in a future PR
  const legacyLocalesToInclude = ['UK', 'EU'];
  const legacyLocalizedProductsRequests = makeLegacyLocalizedRequests({
    contentType: ContentfulTemplates.PRODUCT_PAGE,
    include: legacyLocalesToInclude,
  });
  const legacyLocalizedUseCasesRequests = makeLegacyLocalizedRequests({
    contentType: ContentfulTemplates.USE_CASE,
    include: legacyLocalesToInclude.concat(['US']), // allows us to remove `/use-cases/[index].jsx`
  });
  const legacyLocalizedLandingPageRequests = makeLegacyLocalizedRequests({
    contentType: ContentfulTemplates.LANDING_PAGE,
    include: legacyLocalesToInclude.concat(['US']), // yes, we want US because we no longer have [index]
  });

  // we fetch from the new template for every supported locale
  // locales must first be enabled in Contentful and then added to the
  // ContentfulLocales set in src/lib/constants.ts
  const newTemplateRequests = makeNewTemplateRequestsForAllLocales({
    'fields.url[nin]': ['homepage'], // we filter out the homepage entries
    include: 1,
    // these are required fields in the template and required for production
    queryByLocale: {
      'en-EU': {
        'fields.components[exists]': true,
        'fields.url[exists]': true,
        'fields.metaTitle[exists]': true,
        'fields.metaDescription[exists]': true,
      },
      'en-GB': {
        'fields.components[exists]': true,
        'fields.url[exists]': true,
        'fields.metaTitle[exists]': true,
        'fields.metaDescription[exists]': true,
      },
    },
  });

  const [productRoutes, responses] = await Promise.all([
    getProductStaticPaths(),
    Promise.all([
      ...legacyLocalizedProductsRequests,
      ...legacyLocalizedUseCasesRequests,
      ...legacyLocalizedLandingPageRequests,
      ...newTemplateRequests,
    ]),
  ]);
  // The more specific products route owns legacy US product pages. A CMS
  // migration can leave both templates published; Next rejects duplicate paths.
  const reservedProductPaths = new Set(
    productRoutes.paths.map(({ params }) => {
      return `products/${params.index}`;
    }),
  );

  // Filter responses to only include entities with 1 or more components
  const filteredResponses = responses.map((response) => {
    if (response?.items) {
      return {
        ...response,
        items: response.items.filter((item) => {
          return item?.fields?.components?.length >= 1;
        }),
      };
    }
    return response;
  });

  const paths = mapResponseToPaths(filteredResponses);

  // more massaging for hard coded and localized root pages
  // NOTE: maybe one day we remove these?
  hardcodedRootPages.forEach((page) => {
    const locale = page.params.id;
    const slug = page.params.index;
    paths.push({
      params: {
        id: [locale, slug],
      },
    });
  });

  // we used to deduplicate paths for customer stories with uniqWith so that CMS content takes precedence
  // this can hopefully be removed in a future PR once all customer stories have been migrated to the new template
  const deduplicatedPaths = _uniqWith(paths, (arrVal, othVal) => {
    return arrVal.params.id.join(',') === othVal.params.id.join(',');
  }).filter((path) => {
    return (
      path.params.id.length >= 1 &&
      !reservedProductPaths.has(path.params.id.join('/'))
    );
  });

  return {
    paths: deduplicatedPaths,
    fallback: false,
  };
}

// Static build: uses getStaticProps for build-time data fetching
export async function getStaticProps({ params, preview }) {
  const { id } = params;
  const legacyLocale = getLegacyLocaleFromPath(id);
  const pageSlug = getLegacyPageSlugFromPath(id);

  // we unfortunately still have to support legacy template requests
  // these should be considered deprecated and removed in a future PR
  const requests = [];
  requests.push(
    makeSingleLegacyLocalizedRequest({
      contentType: ContentfulTemplates.PRODUCT_PAGE,
      locale: legacyLocale,
      pageSlug,
    }),
  );
  requests.push(
    makeSingleLegacyLocalizedRequest({
      contentType: ContentfulTemplates.USE_CASE,
      locale: legacyLocale,
      pageSlug,
    }),
  );
  requests.push(
    makeSingleLegacyLocalizedRequest({
      contentType: ContentfulTemplates.LANDING_PAGE,
      locale: legacyLocale,
      pageSlug,
    }),
  );

  // removes locale from query
  const pathWithoutLocale = getPathWithoutLocale(id);
  const locale = getLocaleFromPath(id);

  // set a limit so we get all entries that match a path (otherwise the function only returns 1 record)
  // e.g. /customer-stories/ -> we need to get all /customer-stories/ entries because things like /customer-stories/rocket/ is also returned
  const templateRequest = makeNewTemplateRequestsForSingleLocale(locale, {
    'fields.components[exists]': true,
    'fields.url[all]': pathWithoutLocale,
    limit: 1000,
  });

  const [
    legacyLocalizedProductsResponse,
    legacyLocalizedUseCasesResponse,
    legacyLocalizedLandingPageResponse,
    templateResponse,
  ] = await Promise.all([...requests, templateRequest]);

  const newTemplatePage = templateResponse?.items?.filter((page) => {
    return page.fields.url.every((part, index) => {
      return pathWithoutLocale[index] === part;
    });
  });

  // we short circuit on the first matching response
  // where web 2.0 templates take precedence over legacy templates
  const data =
    legacyLocalizedLandingPageResponse?.items?.[0] ??
    newTemplatePage?.[0] ??
    legacyLocalizedProductsResponse?.items?.[0] ??
    legacyLocalizedUseCasesResponse?.items?.[0] ??
    // NOTE: this can be deleted in the future 👇
    findMatchingHardcodedPage({
      pages: hardcodedRootPages,
      locale: legacyLocale,
      slug: pageSlug,
    });

  // check for placeholders that need augmented page data
  // this process will inject pre-defined props into found placeholder components
  await augmentPlaceholderData({ entry: data });

  // we check to see if the page has a BLOG_POST_EXCERPT component
  // if so we then make a request for some additional blog post meta data
  // NOTE: this can be deleted 👇 in the future as Contentful now supports sharing content between spaces
  const blogPostComponent = data?.fields?.components?.find((component) => {
    return (
      component.sys?.contentType?.sys?.id === CmsComponents.BLOG_POST_EXCERPT
    );
  });

  if (!blogPostComponent) {
    return mapEntryToProps({ entry: data, preview });
  }

  try {
    const components = await generateBlogExcerptProps(data, blogPostComponent);
    data.fields.components = components;
    return mapEntryToProps({ entry: data, preview });
  } catch {
    return mapEntryToProps({ entry: data, preview });
  }
}

export default StaticCmsPage;
